import {
  Avatar,
  Button,
  Divider,
  Flex,
  Heading,
  Icon,
  SimpleGrid,
  Tag,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import moment, { weekdays } from 'moment';
import React, { useState } from 'react';
import { Edit, Trash } from 'react-feather';
import { useConfig } from '../../../hooks/useConfig';
import { FirebaseCourseWithUidAndImg } from '../../../types/types';
import { AdminCourseDeleteAlert } from './AdminCourseDeleteAlert';

const MotionFlex = motion(Flex);
interface AdminCourseItemProps {
  course: FirebaseCourseWithUidAndImg;
  editCourse: () => void;
  category?: string;
}

export const AdminCourseItem: React.FC<AdminCourseItemProps> = ({
  course,
  editCourse,
  category,
}) => {
  const isMobile = useBreakpointValue([true, true, false]);
  const courseStart = moment(course.start);
  const courseEnd = moment(course.start).add(course.duration, 'minutes');
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);

  return (
    <>
      <MotionFlex
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        borderRadius="xl"
        w="full"
        overflow="hidden"
        p={4}
        mb={6}
        boxShadow="md"
        direction="column"
      >
        <Flex align="center" justify="space-between">
          <Flex align="center">
            {!isMobile && (
              <Avatar
                mr={6}
                src={course.img}
                name={course.title}
                borderRadius="3px"
                size="xl"
              />
            )}
            <Flex wrap="nowrap" mb={[3, 3, 0]} direction="column">
              <Heading size="md" mb={2} fontWeight="bold">
                {course.title}
              </Heading>
              <Text mb={2} fontWeight="bold" whiteSpace="nowrap">
                {courseStart.format('LT')}
                {' - '}
                {courseEnd.format('LT')}
              </Text>

              <Text noOfLines={1}>{course.description}</Text>
              {category && (
                <Tag mt={3} alignSelf="flex-start">
                  Kategorie: {category}
                </Tag>
              )}
            </Flex>
          </Flex>
          <Flex direction="column" align="flex-end">
            {course.recurrence.isRecurring && (
              <Flex align="center" flexWrap="wrap" justify="flex-end">
                {course.recurrence.daysOfWeek.length > 0 &&
                  course.recurrence.daysOfWeek
                    .filter(Boolean)
                    .map((day: any, idx: number) => (
                      <Tag
                        size="lg"
                        key={`weekday-tag${courseStart.unix()}-${+idx.toString()}`}
                        ml={2}
                        my={1}
                      >
                        {weekdays(true)[+day].slice(0, 2)}
                      </Tag>
                    ))}
              </Flex>
            )}
            <Flex
              wrap="wrap"
              justify="flex-end"
              mb={[3, 3, 0]}
              ml={[0, 0, 2]}
              mt={4}
            >
              {course.isOnline && (
                <Tag
                  ml={1}
                  mb={2}
                  whiteSpace="nowrap"
                  colorScheme="green"
                  size="lg"
                  variant="outline"
                >
                  Online-Kurs
                </Tag>
              )}
              {course.canRegister && (
                <Tag
                  ml={1}
                  mb={2}
                  whiteSpace="nowrap"
                  colorScheme="green"
                  size="lg"
                  variant="outline"
                >
                  Studio-Kurs (max. {course.maxParticipants})
                </Tag>
              )}
            </Flex>
          </Flex>
        </Flex>
        <Divider my={4} />
        <SimpleGrid columns={[1, 1, 2]} gap={4}>
          <Button
            variant="ghost"
            leftIcon={<Icon as={Edit} />}
            isFullWidth={isMobile}
            onClick={editCourse}
          >
            Kurs bearbeiten
          </Button>

          <Button
            variant="ghost"
            colorScheme="red"
            leftIcon={<Icon as={Trash} />}
            onClick={() => setIsDeleteAlertOpen(true)}
            isFullWidth={isMobile}
          >
            Kurs löschen
          </Button>
        </SimpleGrid>
      </MotionFlex>
      <AdminCourseDeleteAlert
        isOpen={isDeleteAlertOpen}
        onClose={() => setIsDeleteAlertOpen(false)}
        firebaseCourse={course}
      />
    </>
  );
};
